import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import React, { useEffect } from "react";
import { useUser } from "../contexts/useUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api";
import {
  callLogFilesByProjectIdAndCreatedAt,
  getLLMProject,
} from "../graphql/queries";
import {
  CallLogFilesByProjectIdAndCreatedAtQuery,
  GetLLMProjectQuery,
} from "../API";
import { updateLLMProjectLogging } from "../dataaccess/updateProjectLogging";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectElement, TextFieldElement } from "react-hook-form-mui";

let schema = z.object({
  logfileRetentionPeriod: z
    .number()
    .int()
    .min(0)
    .max(365)
    .optional()
    .nullable(),
  callRetentionPeriod: z.number().int().min(1).max(365).optional().nullable(),
  logInterval: z.string().optional().nullable(),
});

type LoggingFormType = z.infer<typeof schema>;

const Logging: React.FC = () => {
  const { project, entitlements, trialDaysRemaining } = useUser();
  const { logError } = useErrorLogging();
  const [maximumDaysAllowed, setMaximumDaysAllowed] =
    React.useState<number>(45);

  const modifySchema = (
    schema: any,
    maxLogRetentionDays: number,
    maxCallRetentionDays: number
  ) => {
    return schema.extend({
      logfileRetentionPeriod: z
        .number()
        .int()
        .min(0)
        .max(maxLogRetentionDays)
        .optional()
        .nullable(),
      callRetentionPeriod: z
        .number()
        .int()
        .min(1)
        .max(maxCallRetentionDays)
        .optional()
        .nullable(),
    });
  };

  useEffect(() => {
    if (!project) return;

    let result = 45;

    if (entitlements && entitlements.length > 0) {
      if (entitlements?.includes("logging-retention-tier-3")) result = 180;
      else if (entitlements?.includes("logging-retention-tier-2")) result = 90;
      else if (entitlements?.includes("logging-retention-tier-1")) result = 45;
    }

    schema = modifySchema(schema, result, result);
    setMaximumDaysAllowed(result);
  }, [entitlements]);

  const queryClient = useQueryClient();

  const mutationUpdateProject = useMutation({
    mutationFn: updateLLMProjectLogging,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => {
      logError("Error updating project", error as any);
    },
  });

  const {
    data: projectData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["projects", { id: project?.id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(getLLMProject, { id: project?.id })
      ) as Promise<GraphQLResult<GetLLMProjectQuery>>,
    enabled: !!project?.id,
  });

  const {
    data: callLogData,
    isLoading: callLogLoading,
    isError: callLogIsError,
  } = useQuery({
    queryKey: ["callLogs", { id: project?.id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(callLogFilesByProjectIdAndCreatedAt, {
          projectId: project?.id,
          sortDirection: "DESC",
        })
      ) as Promise<GraphQLResult<CallLogFilesByProjectIdAndCreatedAtQuery>>,
    enabled: !!project?.id,
  });

  const { handleSubmit, control, formState, getValues } =
    useForm<LoggingFormType>({
      values: projectData?.data?.getLLMProject ?? {},
      resolver: zodResolver(schema),
      mode: "onChange",
    });

  if (callLogLoading) return <div>Loading...</div>;
  if (callLogIsError) return <div>Error loading data</div>;

  const callLogs =
    callLogData?.data?.callLogFilesByProjectIdAndCreatedAt?.items ?? [];

  const onSubmit = (data: LoggingFormType) => {
    handleUpdate(data);
  };

  const handleUpdate = (values: LoggingFormType) => {
    if (!values) return;

    mutationUpdateProject.mutate({
      id: project?.id ?? "",
      logfileRetentionPeriod: values.logfileRetentionPeriod,
      callRetentionPeriod: values.callRetentionPeriod,
      logInterval: values.logInterval,
    });
  };

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Paper elevation={3} style={{ borderRadius: 2 }}>
          <Box p={2}>
            <Box
              p={2}
              sx={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  marginLeft: 1,
                  marginTop: 0.5,
                }}
              >
                <TextFieldElement
                  control={control}
                  label="Log retention days"
                  type="number"
                  size="small"
                  sx={{ margin: 0.5, width: "150px", maxWidth: "150px" }}
                  inputProps={{
                    min: 30,
                    max: maximumDaysAllowed,
                  }}
                  name="logfileRetentionPeriod"
                />
                <TextFieldElement
                  control={control}
                  label="Call retention days"
                  type="number"
                  size="small"
                  sx={{ margin: 0.5, width: "150px", maxWidth: "150px" }}
                  inputProps={{
                    min: 45,
                    max: maximumDaysAllowed,
                  }}
                  name="callRetentionPeriod"
                />

                <SelectElement
                  control={control}
                  name="logInterval"
                  label="Generate logs"
                  size="small"
                  sx={{ margin: 0.5, width: "150px", maxWidth: "150px" }}
                  SelectProps={{
                    native: true,
                  }}
                  options={[
                    { id: "none", label: "None" },
                    { id: "daily", label: "Daily" },
                    { id: "weekly", label: "Weekly" },
                  ]}
                />

                <Button
                  variant="outlined"
                  sx={{ margin: 0.5 }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={!formState.isDirty}
                >
                  Update
                </Button>
              </Box>
              {project?.lastLogGeneratedAt && (
                <Typography variant="caption" sx={{ marginLeft: 1.5 }}>
                  Last scheduled log at:{" "}
                  {new Date(project?.lastLogGeneratedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
        <Paper elevation={3} style={{ borderRadius: 2 }}>
          {callLogs?.map((item) => (
            <List key={item?.id}>
              <ListItem dense>
                <ListItemText
                  primary={
                    <Typography
                      style={{
                        textDecoration:
                          item?.status === "deleted" ? "line-through" : "none",
                      }}
                    >
                      {`${
                        (item?.key ?? "").includes("scheduled")
                          ? "Scheduled Log"
                          : "Manual Log"
                      } of calls between: ${new Date(
                        item?.fromDate ?? ""
                      ).toLocaleString()} - ${new Date(
                        item?.toDate ?? ""
                      ).toLocaleString()}`}
                    </Typography>
                  }
                  secondary={`Generated ${new Date(
                    item?.createdAt ?? ""
                  ).toLocaleString()} with ${
                    item?.count ?? 0
                  } calls. File will be deleted after ${new Date(
                    item?.deleteFileAfter ?? ""
                  ).toLocaleString()}`}
                />
                {item?.status === "active" && (
                  <ListItemButton
                    style={{ width: "200px", maxWidth: "300px" }}
                    onClick={async () => {
                      let urlToOpen = item?.url ?? "";

                      if (new Date(item?.urlExpiresAt ?? "") < new Date()) {
                        console.log("url expired");
                        try {
                          const r = await API.post(
                            "eventapi",
                            "/generateSecureUrlForCallLogFile",
                            {
                              body: {
                                projectId: project?.id,
                                callLogFileId: item?.id,
                                urlExpiry: 3600,
                              },
                            }
                          );

                          queryClient.invalidateQueries({
                            queryKey: ["callLogs"],
                          });
                          urlToOpen = r.url;
                        } catch (e) {
                          console.error(e);
                        }
                      }

                      if (!urlToOpen || urlToOpen === "") return;

                      window.open(urlToOpen ?? "", "_blank");
                    }}
                  >
                    <div>
                      <Typography variant="body2">Download File</Typography>

                      {new Date(item?.urlExpiresAt ?? "") < new Date() ? (
                        <Typography variant="caption">
                          url expired, click to create new URL
                        </Typography>
                      ) : (
                        <Typography variant="caption">{`URL expires ${new Date(
                          item?.urlExpiresAt ?? ""
                        ).toLocaleString()}`}</Typography>
                      )}
                    </div>
                  </ListItemButton>
                )}
              </ListItem>
            </List>
          ))}
        </Paper>
      </Stack>
    </Box>
  );
};

export default Logging;
